import { useEffect, useMemo, useState } from "react";

import {
  fetchProcessorMatrix,
  fetchUserRights,
  fetchUserRightsContext,
  saveProcessorMatrix,
  saveUserRights,
} from "../../../api";

import Swal from "sweetalert2";

const RIGHTS_FIELDS = [
  { key: "invoice_date_edit", label: "Invoice Date Edit" },
  { key: "gsttaxcode_edit", label: "GST Tax Code Edit" },
  { key: "withholdingtaxcode_edit", label: "Withholding Tax Code Edit" },
  { key: "material_service_edit", label: "Material/Service Edit" },
  { key: "unallocatedcost_edit", label: "Unallocated Cost Edit" },
  { key: "narration_edit", label: "Narration Edit" },
  { key: "invoicenumber_edit", label: "Invoice Number Edit" },
];

const ROLE_OPTIONS = [
  { value: "processor", label: "Processor" },
  { value: "uploader", label: "Uploader" },
  { value: "data_uploader", label: "Data Uploader" },
  { value: "checker", label: "Checker" },
];

const PROCESSOR_PO_OPTIONS = ["Both", "PO", "Non PO"];
const PROCESSOR_MATERIAL_OPTIONS = ["Both", "Material", "Service"];

function createDefaultRights() {
  const next = {};
  RIGHTS_FIELDS.forEach((field) => {
    next[field.key] = false;
  });
  return next;
}

export default function ApUserRightsView() {
  const [activeTab, setActiveTab] = useState("rights");
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [supportedRoles, setSupportedRoles] = useState(["processor"]);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedRole, setSelectedRole] = useState("processor");
  const [rightsByEmail, setRightsByEmail] = useState({});
  const [processorMatrix, setProcessorMatrix] = useState([]);
  const [loadingProcessorMatrix, setLoadingProcessorMatrix] = useState(true);
  const [savingProcessorMatrix, setSavingProcessorMatrix] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const filteredUsers = useMemo(() => {
    if (!selectedTeam) {
      return [];
    }

    const normalizedRole = selectedRole.toLowerCase();
    return users.filter((user) => {
      const userRoles = Array.isArray(user.role) ? user.role : [];
      const userTeams = Array.isArray(user.team) ? user.team : [];
      return (
        userTeams.includes(selectedTeam) &&
        userRoles.some((role) => String(role || "").toLowerCase() === normalizedRole)
      );
    });
  }, [selectedRole, selectedTeam, users]);

  const isRoleSupported = supportedRoles.includes(selectedRole);
  const activeUserCount = activeTab === "rights" ? filteredUsers.length : processorMatrix.length;

  useEffect(() => {
    async function loadContext() {
      setLoading(true);
      setError("");

      try {
        const payload = await fetchUserRightsContext();
        const nextTeams = payload.teams || [];
        setTeams(nextTeams);
        setUsers(payload.users || []);
        setSupportedRoles(payload.supportedRoles || ["processor"]);
        setSelectedTeam((current) => current || nextTeams[0]?.team || "");
      } catch (requestError) {
        setError(requestError.message || "Unable to load user rights context.");
      } finally {
        setLoading(false);
      }
    }

    loadContext();
  }, []);

  useEffect(() => {
    async function loadProcessorMatrix() {
      if (!selectedTeam) {
        setProcessorMatrix([]);
        setLoadingProcessorMatrix(false);
        return;
      }

      setLoadingProcessorMatrix(true);
      try {
        const payload = await fetchProcessorMatrix(selectedTeam);
        setProcessorMatrix(payload || []);
      } catch (requestError) {
        setError(requestError.message || "Unable to load processor matrix.");
      } finally {
        setLoadingProcessorMatrix(false);
      }
    }

    loadProcessorMatrix();
  }, [selectedTeam]);

  useEffect(() => {
    async function loadRights() {
      if (!selectedTeam || !isRoleSupported) {
        setRightsByEmail({});
        return;
      }

      setError("");
      setStatus("");

      try {
        const payload = await fetchUserRights(selectedTeam, selectedRole);
        const next = {};

        filteredUsers.forEach((user) => {
          const email = user.email || "";
          if (!email) {
            return;
          }

          next[email] = {
            ...createDefaultRights(),
            ...(payload.data?.[email] || {}),
          };
        });

        setRightsByEmail(next);
      } catch (requestError) {
        setRightsByEmail({});
        setError(requestError.message || "Unable to load user rights.");
      }
    }

    loadRights();
  }, [filteredUsers, isRoleSupported, selectedRole, selectedTeam]);

  function toggleRight(email, key) {
    setRightsByEmail((current) => ({
      ...current,
      [email]: {
        ...createDefaultRights(),
        ...(current[email] || {}),
        [key]: !(current[email] || {})[key],
      },
    }));
  }

  function updateProcessorRow(email, field, value) {
    setProcessorMatrix((current) =>
      current.map((row) =>
        row.email === email
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  }

  async function handleSave() {
    setStatus("");
    setError("");

    if (!selectedTeam) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select a team.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!isRoleSupported) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Only processor rights are supported right now.",
        confirmButtonText: "OK",
      });
      return;
    }

    setSaving(true);
    try {
      const assignments = {};
      filteredUsers.forEach((user) => {
        const email = user.email || "";
        if (!email) {
          return;
        }

        assignments[email] = {
          ...createDefaultRights(),
          ...(rightsByEmail[email] || {}),
        };
      });

      const payload = await saveUserRights({
        team: selectedTeam,
        role: selectedRole,
        assignments,
      });

      const skippedUsers = payload.skippedUsers || [];
      if (skippedUsers.length) {
        // setStatus(`Rights saved with ${skippedUsers.length} skipped user record(s).`);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Rights saved with ${skippedUsers.length} skipped user record(s).",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Rights saved successfully.",
          confirmButtonText: "OK",
        });
      }
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to save rights.",
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveProcessorMatrix() {
    setStatus("");
    setError("");
    setSavingProcessorMatrix(true);

    try {
      const payload = await saveProcessorMatrix(processorMatrix, selectedTeam);
      const skippedUsers = payload.skippedUsers || [];
      if (skippedUsers.length) {
        setStatus(`Processor matrix saved with ${skippedUsers.length} skipped user record(s).`);
      } else {
        setStatus(payload.message || "Processor matrix saved successfully.");
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to save processor matrix.");
    } finally {
      setSavingProcessorMatrix(false);
    }
  }

  return (
    <section className="users-view">
      <div className="users-view__hero">
        <div>
          <p className="eyebrow">User Rights</p>
          <h2>User Rights Matrix</h2>
          <p className="config-view__intro">
            Configure user-level edit permissions by team and role. Processor profiles are loaded from the processing profile table.
          </p>
        </div>
        <div className="users-view__summary">
          <span className="users-view__summary-count">{activeUserCount}</span>
          <span className="users-view__summary-label">
            {activeTab === "rights" ? "users in scope" : "processors in scope"}
          </span>
        </div>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="workspace-panel">
        <div className="processor-table__header">
          <div>
            <p className="eyebrow">Tabs</p>
            <h2>User Rights Configuration</h2>
          </div>
        </div>

        <div className="template-tabs">
          <button
            className={`template-tab${activeTab === "rights" ? " is-active" : ""}`}
            type="button"
            onClick={() => setActiveTab("rights")}
          >
            User Rights
          </button>
          <button
            className={`template-tab${activeTab === "processors" ? " is-active" : ""}`}
            type="button"
            onClick={() => setActiveTab("processors")}
          >
            Processors
          </button>
        </div>

        <div className="config-inline">
          <label className="config-field">
            <span>Team</span>
            <select value={selectedTeam} onChange={(event) => setSelectedTeam(event.target.value)}>
              <option value="">-- Select Team --</option>
              {teams.map((team) => (
                <option key={team.id || team.team} value={team.team}>
                  {team.team_name || team.team}
                </option>
              ))}
            </select>
          </label>

          {activeTab === "rights" ? (
            <label className="config-field">
              <span>Role</span>
              <select value={selectedRole} onChange={(event) => setSelectedRole(event.target.value)}>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {activeTab === "rights" ? (
          <>
            <div className="processor-table__header">
              <div>
                <p className="eyebrow">Rights Matrix</p>
                <h2>User-wise edit permissions</h2>
              </div>
              <button
                className="primary-button"
                type="button"
                disabled={saving || loading || !isRoleSupported}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save Rights"}
              </button>
            </div>

            {!isRoleSupported ? (
              <p className="config-note">Selected role is not backed by the API yet. Processor is the only supported role today.</p>
            ) : !selectedTeam ? (
              <p className="config-note">Select a team to configure rights.</p>
            ) : filteredUsers.length ? (
              <div className="config-table">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      {RIGHTS_FIELDS.map((field) => (
                        <th key={field.key}>{field.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => {
                      const email = user.email || "";
                      const rights = rightsByEmail[email] || createDefaultRights();

                      return (
                        <tr key={user.id || email}>
                          <td>{user.first_name || email}</td>
                          {RIGHTS_FIELDS.map((field) => (
                            <td key={`${email}-${field.key}`}>
                              <input
                                type="checkbox"
                                checked={Boolean(rights[field.key])}
                                onChange={() => toggleRight(email, field.key)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-state">No users found for this team and role.</p>
            )}
          </>
        ) : (
          <>
            <div className="processor-table__header">
              <div>
                <p className="eyebrow">Processor Matrix</p>
                <h2>Processor assignment settings</h2>
              </div>
              <button
                className="primary-button"
                type="button"
                disabled={savingProcessorMatrix || loadingProcessorMatrix}
                onClick={handleSaveProcessorMatrix}
              >
                {savingProcessorMatrix ? "Saving..." : "Save Processor Matrix"}
              </button>
            </div>

            {loadingProcessorMatrix ? (
              <p className="config-note">Loading processor matrix...</p>
            ) : processorMatrix.length ? (
              <div className="config-table">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Teams</th>
                      <th>PO vs Non PO</th>
                      <th>Value Lower Limit</th>
                      <th>Value Higher Limit</th>
                      <th>Material vs Services</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processorMatrix.map((row) => (
                      <tr key={row.id || row.email}>
                        <td>{row.user}</td>
                        <td>{row.email}</td>
                        <td>{(row.teams || []).join(", ") || "-"}</td>
                        <td>
                          <select
                            value={row.po_type || "Both"}
                            onChange={(event) => updateProcessorRow(row.email, "po_type", event.target.value)}
                          >
                            {PROCESSOR_PO_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.lower_limit ?? 0}
                            onChange={(event) =>
                              updateProcessorRow(row.email, "lower_limit", Number(event.target.value || 0))
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.higher_limit ?? -1}
                            onChange={(event) =>
                              updateProcessorRow(row.email, "higher_limit", Number(event.target.value || -1))
                            }
                          />
                        </td>
                        <td>
                          <select
                            value={row.material_type || "Both"}
                            onChange={(event) => updateProcessorRow(row.email, "material_type", event.target.value)}
                          >
                            {PROCESSOR_MATERIAL_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="empty-state">No processor users found for this team.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
